import { MouseEventHandler, MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
// Materials
import { Collapse, Divider, Grid } from "@mui/material";
import { useGridApiRef, GridValidRowModel } from "@mui/x-data-grid-premium";
// Helpers
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getColumnDefinitions, getGroupingColDef } from "./columnsDefinition";
import { nullishResolver } from "@/Common/helpers/sonar";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
// Hooks
import useAggregationExtended from "@/Layouts/components/Tables/DataSmartTable/aggregation/Aggregation";
// Types & Constants
import { Issue } from "@/Common/types/tables";
import { CustomColDef } from "@/Washouts/types/commons";
import { IPerformanceTable, ITechnicianPerformanceTable } from "@/Washouts/types/technicians";
import { DEFAULT_COLLAPSED_SIZE } from "@/Washouts/constants";

const getTreeDataPath = (row: GridValidRowModel) => row.hierarchy;

const resetApiFilter = (apiRef: ReturnType<typeof useGridApiRef>) => {
	try {
		if (apiRef.current?.state?.filter) {
			const cleanFilter = {
				filterModel: { items: [], linkOperator: "and" },
				visibleRows: null,
				visibleRowsLookup: {},
				filteredRowsLookup: {},
				filteredDescendantCountLookup: {},
				filteredChildrenCountLookup: {},
			};

			apiRef.current.setState((prevState) => ({
				...prevState,
				filter: cleanFilter,
			}));
		}
	} catch (error) {
		errorLogger(getError(error));
	}
};

const getRowModels = (apiRef: ReturnType<typeof useGridApiRef>) => apiRef?.current?.getRowModels?.() ?? new Map();

const resolveRowsFromRowModels = (rowsAsMap: Map<unknown, GridValidRowModel>) => {
	const rows = [];
	for (const value of rowsAsMap.values()) {
		rows.push(value);
	}
	return rows;
};

const shouldShowUnfilteredData = (apiRef: ReturnType<typeof useGridApiRef>) => !apiRef?.current?.state?.filter?.filterModel?.items?.length;

const resolveNoRowsOverlay = (title: string) => {
	let text = "No Performance Data";
	switch (title) {
		case "Flagged Time Performance":
			text = "No Flagged Time Performance Data";
			break;
		case "Pending Repair Orders and Jobs":
			text = "No Pending Repair Orders and Jobs Data";
			break;
		default:
			break;
	}
	return text;
};

function updateCurrentIssue(activeIssue: MutableRefObject<Issue>, issuesRef?: string, color?: string, currentIssues: Issue[] = []) {
	let isPresent = false;
	for (const issue of currentIssues) {
		if (
			issue &&
			issue.rowReference === activeIssue?.current?.rowReference &&
			issue.columnReference === activeIssue?.current?.columnReference
		) {
			issue.color = color;
			isPresent = true;
			break;
		}
	}

	if (!isPresent) {
		currentIssues.push({
			tableReference: issuesRef,
			rowReference: nullishResolver(activeIssue?.current?.rowReference, ""),
			columnReference: nullishResolver(activeIssue?.current?.columnReference, ""),
			color,
		});
	}
	return currentIssues;
}

function PerformanceTable(props: Readonly<IPerformanceTable>) {
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<CustomColDef[]>([]);
	const [rows, setRows] = useState<GridValidRowModel[]>([]);
	const [collapsed, setCollapsed] = useState(true);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			currentIssues = updateCurrentIssue(activeIssue, props.issuesReference, color, currentIssues);
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	const aggregations = useAggregationExtended({
		selectedSum: {},
		technicianCustomRate: {},
	});

	useMemo(() => {
		setTotalColumns(getColumnDefinitions(issues));
	}, [issues]);

	useEffect(() => {
		// Clear filter on purpose
		resetApiFilter(apiRef);

		setIssues(nullishResolver(props.issues, []));
		setCollapsed(Boolean(props?.collapsed));

		const rowsAsMap = getRowModels(apiRef);
		setRows(resolveRowsFromRowModels(rowsAsMap));
		if (shouldShowUnfilteredData(apiRef)) {
			setRows(nullishResolver(props?.data, []));
		}

		return () => {
			setCollapsed(true);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props.issues, props.data, props.collapsed, apiRef]);

	const showTableConditions = [Boolean(totalColumns?.length), Boolean(apiRef)];
	if (showTableConditions.some((condition) => !condition)) {
		return null;
	}

	const footerModels = {
		actualTime: "technicianCustomSum",
		bookedTime: "technicianCustomSum",
		bonusTime: "technicianCustomSum",
		extended: "technicianCustomSum",
		extendedBonus: "technicianCustomSum",
		rate: "technicianCustomRate",
		finishDate: "technicianUniqueCount",
		operationCode: "technicianUniqueCount",
		reason: "technicianUniqueCount",
		jobStatus: "technicianUniqueCount",
		payrollStatus: "technicianUniqueCount",
		job: "technicianUniqueCount",
	};

	const groupingColDef = getGroupingColDef(issues);

	return (
		<>
			<Collapse in={!collapsed} collapsedSize={DEFAULT_COLLAPSED_SIZE} timeout={100}>
				{showTableConditions ? (
					<>
						<DataSmartTable
							{...props.tableProps}
							apiRef={apiRef}
							columns={totalColumns}
							rows={rows ?? []}
							hideFooter
							disableColumnMenu
							disableRowSelectionOnClick
							disableMultipleRowSelection
							checkboxSelection={false}
							columnVisibilityModel={{
								_id: false,
							}}
							initialState={{
								aggregation: {
									model: footerModels,
								},
							}}
							slots={{
								toolbar: CommonToolbar,
								noRowsOverlay: NoRowsOverlay,
							}}
							slotProps={{
								toolbar: {
									titleExpandible: {
										collapsed,
										hide: false,
										collapsible: true,
										title: props?.title,
										titleClasses: "page-title",
										sxContainer: { padding: "20px 32px" },
										onClick: setCollapsed,
									},
									filter: {
										hide: false,
									},
									search: {
										useQuickFilter: true,
									},
									expand: {
										hide: true,
									},
								},
								noRowsOverlay: {
									title: resolveNoRowsOverlay(props?.title ?? ""),
								},
								cell: props?.issues ? { onClick: handleClickCell } : undefined,
							}}
							customProps={{
								tableClassName: {
									"[&_.MuiDataGrid-main]:mx-[30px] [&_.MuiDataGrid-main]:w-[1340px]": true,
									"[&_.toolbar-actions]:absolute [&_.toolbar-actions]:top-[15px] [&_.toolbar-actions]:right-[30px]": true,
									// Improve overlay look when no rows after filtering
									"[&_.MuiDataGrid-overlay]:font-montserrat": true,
									"[&_.MuiDataGrid-overlay]:text-[16px]": true,
									"[&_.MuiDataGrid-overlay]:text-[color:--text-color]": true,
								},
								customAggregationsFn: { ...aggregations },
							}}
							treeData
							getTreeDataPath={getTreeDataPath}
							groupingColDef={groupingColDef}
							defaultGroupingExpansionDepth={1}
							disableChildrenFiltering={false}
						/>
						{props.addIssues ? (
							<AddCellIssues
								open={openAddIssues}
								anchorEl={anchorAddIssues}
								onCloseAction={() => {
									setOpenAddIssues(false);
									setAnchorAddIssues(null);
								}}
								onChangeAction={handleChangeIssues}
							/>
						) : null}
					</>
				) : null}
			</Collapse>
			<Collapse in={Boolean(props.divider)} collapsedSize={0} timeout={100}>
				<Divider
					className={clsx({
						"!mx-[24px] !border-b !border-[#CCC] !py-[5px] !mt-[24px]": !props.sxSectionDivider && !collapsed,
						"!mx-[24px] !border-b !border-[#CCC] !pt-0": !props.sxSectionDivider && collapsed,
					})}
					sx={props.sxSectionDivider}
				></Divider>
			</Collapse>
		</>
	);
}

export default function TechnicianPerformanceTable(props: Readonly<ITechnicianPerformanceTable>) {
	if (props.loading) {
		return (
			<>
				<Grid className="py-[20px] px-[32px]">
					<TableLoader smallTitle toolbar />
				</Grid>
				{props.divider && (
					<Divider
						className={clsx({
							"!mx-[24px] !border-b !border-[#CCC]": !props.sxSectionDivider,
						})}
						sx={props.sxSectionDivider}
					></Divider>
				)}
			</>
		);
	}

	return <>{props.dataPresent && <PerformanceTable {...props} divider />}</>;
}
